import React, { useEffect, useState } from 'react';
import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import Button from '../../components/common/Button';
import Drawer from '../../components/common/Drawer';
import AddNewButton from '../../components/common/AddButton';
import CompetitionExerciseTable from '../../components/tables/CompetitionExerciseTable';
import CompetitionExerciseForm from './CompetitionExerciseForm';
import {
  CompetitionExerciseDTO,
  ExerciseSpecificDTO,
  ExerciseTargetDTO,
  ExerciseTemplateDTO,
  FirearmCalibreDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useExerciseTemplateApi } from '../../hooks/useExerciseTemplateApi';
import { useExerciseTargetApi } from '../../hooks/useExerciseTargetApi';
import { useFirearmCalibres } from '../../hooks/useFirearmCalibreApi';
import { useCompetitionExerciseApi } from '../../hooks/useCompetitionExerciseApi';
import { confirmDelete } from '../../utils/sweetAlertUtils';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';

interface ExerciseSpecificFormProps {
  initialValues: ExerciseSpecificDTO;
  onSubmit: (data: ExerciseSpecificDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  compliance: {
    id: 'compliance',
    title: 'Compliance Settings',
  },
  evaluation: {
    id: 'evaluation',
    title: 'Evaluation Settings',
  },
  activity: {
    id: 'activity',
    title: 'Activity Settings',
  },
  targets: {
    id: 'targets',
    title: 'Target Settings',
  },
  competition: {
    id: 'competition',
    title: 'Competition Eligibility',
  },
  competitionExercises: {
    id: 'competition-exercises',
    title: 'Competition Exercises',
  },
} as const;

const ExerciseSpecificForm: React.FC<ExerciseSpecificFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<ExerciseSpecificDTO>(initialValues);

  const [exerciseTemplates, setExerciseTemplates] = useState<ExerciseTemplateDTO[]>([]);
  const exerciseTemplateApi = useExerciseTemplateApi();

  const { search: searchExerciseTargets } = useExerciseTargetApi();
  const [exerciseTargetOptions, setExerciseTargetOptions] = useState<
    { value: string; label: string | React.ReactNode }[]
  >([]);
  const [exerciseTargetMap, setExerciseTarget] = useState<
    Map<string, ExerciseTargetDTO>
  >(new Map());

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    exerciseTemplateId: [{ required: true, message: 'Template is required' }],
  };

  const { errors, validate } =
    useFormValidation<ExerciseSpecificDTO>(validationRules);

  const { data: calibres = [] } = useFirearmCalibres();
  const [calibreOptions, setCalibreOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [calibreMap, setCalibreMap] = useState<Map<string, FirearmCalibreDTO>>(
    new Map(),
  );

  useEffect(() => {
    if (calibres.length > 0) {
      setCalibreOptions(
        calibres.map((c) => ({ value: c.id as string, label: c.title || '' })),
      );
      setCalibreMap(new Map(calibres.map((c) => [c.id as string, c])));
    }
  }, [calibres]);

  const fetchFirearmTypes = async () => {
    try {
      const response = await exerciseTemplateApi.search({
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      setExerciseTemplates(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  useEffect(() => {
    const loadExerciseTargets = async () => {
      try {
        const response = await searchExerciseTargets({
          page: 1,
          pageSize: 9999,
          sortBy: 'Code',
          sortDescending: false,
        });

        const exerciseTargets =
          (response?.data?.results as ExerciseTargetDTO[]) || [];
        const options = exerciseTargets
          .filter((c) => c.id)
          .map((c) => ({
            value: c.id as string,
            label: (
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{c.title}</span>
                  {c.code && (
                    <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                      {c.code}
                    </span>
                  )}
                </div>
                {c.description && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {c.description}
                  </span>
                )}
              </div>
            ),
            searchText: `${c.title} ${c.code || ''} ${c.description || ''}`,
          }));

        // Create a map of id to full DTO
        const exerciseTargetMapping = new Map(
          exerciseTargets.map((c) => [c.id as string, c]),
        );

        setExerciseTargetOptions(options);
        setExerciseTarget(exerciseTargetMapping);
      } catch (error) {
        console.error('Error loading exercise targets:', error);
      }
    };

    loadExerciseTargets();
    fetchFirearmTypes();
  }, []);

  const {
    search: searchCompetitionExercises,
    create: createCompetitionExercise,
    edit: editCompetitionExercise,
    remove: removeCompetitionExercise,
    isLoading: isLoadingCompetitionExercises,
  } = useCompetitionExerciseApi();
  const [competitionExercises, setCompetitionExercises] = useState<
    CompetitionExerciseDTO[]
  >([]);
  const [competitionExercisePage, setCompetitionExercisePage] = useState(1);
  const [competitionExercisePageSize, setCompetitionExercisePageSize] =
    useState(10);
  const [competitionExerciseTotal, setCompetitionExerciseTotal] = useState(0);
  const [isCompetitionExerciseDrawerOpen, setIsCompetitionExerciseDrawerOpen] =
    useState(false);
  const [editingCompetitionExercise, setEditingCompetitionExercise] =
    useState<CompetitionExerciseDTO | null>(null);

  const fetchCompetitionExercises = async (page: number, size: number) => {
    if (!formData.id) return;
    try {
      const response = await searchCompetitionExercises({
        filters: { exerciseSpecificId: formData.id },
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setCompetitionExercises(response?.data?.results || []);
      setCompetitionExerciseTotal(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Competition Exercises:', error);
    }
  };

  useEffect(() => {
    fetchCompetitionExercises(competitionExercisePage, competitionExercisePageSize);
  }, [formData.id, competitionExercisePage, competitionExercisePageSize]);

  const handleAddCompetitionExercise = () => {
    setEditingCompetitionExercise({
      exerciseSpecificId: formData.id,
      exerciseSpecificTitle: formData.title,
      isActive: true,
    } as CompetitionExerciseDTO);
    setIsCompetitionExerciseDrawerOpen(true);
  };

  const handleEditCompetitionExercise = (row: CompetitionExerciseDTO) => {
    setEditingCompetitionExercise(row);
    setIsCompetitionExerciseDrawerOpen(true);
  };

  const handleDeleteCompetitionExercise = async (
    row: CompetitionExerciseDTO,
  ) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await removeCompetitionExercise(row.id || '');
      await fetchCompetitionExercises(
        competitionExercisePage,
        competitionExercisePageSize,
      );
    } catch (error) {
      console.error('Error deleting Competition Exercise:', error);
    }
  };

  const handleSubmitCompetitionExercise = async (
    data: CompetitionExerciseDTO,
  ) => {
    try {
      if (data.id) {
        await editCompetitionExercise(data.id, data);
      } else {
        await createCompetitionExercise(data);
      }
      setIsCompetitionExerciseDrawerOpen(false);
      setEditingCompetitionExercise(null);
      await fetchCompetitionExercises(
        competitionExercisePage,
        competitionExercisePageSize,
      );
    } catch (error) {
      console.error('Error saving Competition Exercise:', error);
    }
  };

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS).filter(
    (section) =>
      section.id !== FORM_SECTIONS.competitionExercises.id || !!formData.id,
  );

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required
          error={errors.title}
        />
        <FormField
          id="exerciseTemplateId"
          label="Exercise Template"
          type="dropdown"
          value={formData.exerciseTemplateId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Exercise Template"
          required
          error={errors.templateId}
          options={[
            ...exerciseTemplates.map((type) => ({
              value: type.id,
              label: type.title || '',
            })),
          ]}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.compliance.id}
        title={FORM_SECTIONS.compliance.title}
      >
        <FormField
          id="isComplianceHunt"
          label="Is Compliance Hunt?"
          type="checkbox"
          value={formData.isComplianceHunt || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Enable hunt activity report entry from scores"
        />
        <FormField
          id="isComplianceSport"
          label="Is Compliance Sport?"
          type="checkbox"
          value={formData.isComplianceSport || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Enable sport activity report entry from scores"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.evaluation.id}
        title={FORM_SECTIONS.evaluation.title}
      >
        <FormField
          id="isEvaluationHunt"
          label="Is Evaluation Hunt?"
          type="checkbox"
          value={formData.isEvaluationHunt || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="List exercise in hunt evaluations"
        />
        <FormField
          id="isEvaluationSport"
          label="Is Evaluation Sport?"
          type="checkbox"
          value={formData.isEvaluationSport || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="List exercise in sport evaluations"
        />
        <FormField
          id="isCompetition"
          label="Is Competition?"
          type="checkbox"
          value={formData.isCompetition || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="List exercise in sport competitions"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.activity.id}
        title={FORM_SECTIONS.activity.title}
      >
        <FormField
          id="isHuntActivity"
          label="Is Hunt Activity?"
          type="checkbox"
          value={formData.isHuntActivity || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Score counts as Hunt Activity"
        />
        <FormField
          id="isSportShootingActivity"
          label="Is Sport-Shooting Activity?"
          type="checkbox"
          value={formData.isSportShootingActivity || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Score counts as Sport-Shooting Activity"
        />
        <FormField
          id="isActive"
          label="Is Active?"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to make record active"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.targets.id}
        title={FORM_SECTIONS.targets.title}
      >
        <FormField
          id="targets"
          label="Exercise Targets"
          type="multiselect"
          value={(formData.targets || []).map((c) => c.id as string)}
          onChange={(e) => {
            const selectedIds = Array.isArray(e.target.value)
              ? e.target.value
              : [e.target.value];

            const selectedTargets = selectedIds
              .map((id) => exerciseTargetMap.get(id))
              .filter((c): c is ExerciseTargetDTO => c !== undefined);

            handleChange(
              {
                target: {
                  id: 'targets',
                  value: selectedTargets,
                },
              } as any,
              setFormData,
            );
          }}
          options={exerciseTargetOptions}
          placeholder="Select exercise targets..."
          helper="Select one or more exercise targets"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.competition.id}
        title={FORM_SECTIONS.competition.title}
      >
        <FormField
          id="calibres"
          label="Eligible Calibres"
          type="multiselect"
          value={(formData.calibres || []).map((c) => c.id as string)}
          onChange={(e) => {
            const selectedIds = Array.isArray(e.target.value)
              ? e.target.value
              : [e.target.value];
            const selectedCalibres = selectedIds
              .map((id) => calibreMap.get(id))
              .filter((c): c is FirearmCalibreDTO => c !== undefined);
            handleChange(
              { target: { id: 'calibres', value: selectedCalibres } } as any,
              setFormData,
            );
          }}
          options={calibreOptions}
          placeholder="Select eligible calibres..."
          helper="Calibres a member's firearm must have to submit a competition score for this exercise. Leave empty for exercises with an open-ended calibre range that can't be enumerated as a fixed list."
        />
        <FormField
          id="maxTargetsPerCategory"
          label="Max Targets Per Category"
          type="number"
          value={formData.maxTargetsPerCategory ?? 1}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="1"
          helper="How many scores a member may submit for this exercise within one competition entry (e.g. 2 for exercises allowing two different calibres)."
        />
      </FormSection>

      {formData.id && (
        <FormSection
          id={FORM_SECTIONS.competitionExercises.id}
          title={FORM_SECTIONS.competitionExercises.title}
        >
          <div className="flex justify-end mb-4">
            <AddNewButton onClick={handleAddCompetitionExercise} />
          </div>
          <CompetitionExerciseTable
            data={competitionExercises}
            handleEdit={handleEditCompetitionExercise}
            handleDelete={handleDeleteCompetitionExercise}
            currentPage={competitionExercisePage}
            totalPages={Math.ceil(
              competitionExerciseTotal / competitionExercisePageSize,
            )}
            setCurrentPage={setCompetitionExercisePage}
            pageSize={competitionExercisePageSize}
            setPageSize={setCompetitionExercisePageSize}
            isLoading={isLoadingCompetitionExercises}
          />
        </FormSection>
      )}

      <Drawer
        isOpen={isCompetitionExerciseDrawerOpen}
        onClose={() => {
          setIsCompetitionExerciseDrawerOpen(false);
          setEditingCompetitionExercise(null);
        }}
        title={
          editingCompetitionExercise?.id
            ? 'Edit Competition Exercise'
            : 'Add Competition Exercise'
        }
      >
        {editingCompetitionExercise && (
          <CompetitionExerciseForm
            initialValues={editingCompetitionExercise}
            onSubmit={handleSubmitCompetitionExercise}
            onCancel={() => {
              setIsCompetitionExerciseDrawerOpen(false);
              setEditingCompetitionExercise(null);
            }}
          />
        )}
      </Drawer>
    </Form>
  );
};

export default ExerciseSpecificForm;
